var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import CopyTradingPanelTemplate from '@front/templates/CopyTradingPanelTemplate.html';
import { Account } from '@shared/commons/cache/Account';
import { Connection } from '@shared/commons/Connection';
import { DataCache } from '@shared/commons/DataCache';
import { BaseSettingsUtils } from '@shared/commons/UtilsClasses/BaseGeneralSettingsUtilsWrapper';
import { Resources } from '@shared/localizations/Resources';
import { FollowerRequestStatus } from '@shared/utils/Account/FollowerRequestStatus';
import { getAccountModeText, getAccountModeWidth } from '@shared/utils/cache/Accounts/accounts.utils';
import { ConnectionStates } from '@shared/utils/Connections/connection.types';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { ConfirmationTypesEnum } from '@shared/utils/Trading/ConfirmationTypesEnum';
import { ApplicationPanel } from './ApplicationPanel';
import { TerceraMessageBox } from '../screen/TerceraMessageBox';
import { PanelLocKeys, PanelNames } from '../UtilsClasses/FactoryConstants';
export class CopyTradingPanel extends ApplicationPanel {
    constructor() {
        super();
        this.connectionStateChange = () => {
            const state = Connection.getState();
            switch (state) {
                case ConnectionStates.CONNECTION_LOST:
                case ConnectionStates.DISCONNECTED:
                    this.close();
                    break;
            }
        };
        this.headerLocaleKey = PanelLocKeys[this.getType()];
    }
    getType() {
        return PanelNames.CopyTradingPanel;
    }
    oninit() {
        super.oninit();
        this.observe('visible', () => {
            this.refreshPanelData();
        });
        Account.FollowerIDsChanged.Subscribe(this.refreshPanelData, this);
        Account.TradeStatusChanged.Subscribe(this.refreshPanelData, this);
    }
    oncomplete() {
        super.oncomplete();
        DataCache.OnAddAccount.Subscribe(this.refreshPanelData, this);
        DataCache.OnAccountFollowerResponse.Subscribe(this.onAccountFollowerResponse, this);
        Connection.subscribeOnConnectStateChange(this.connectionStateChange);
        this.refreshPanelData();
        this.center();
        this.localize();
    }
    localize() {
        super.localize();
        void this.set({
            fromAccountText: Resources.getResource('panel.CopyTrading.item.fromAccount'),
            toAccountsText: Resources.getResource('panel.CopyTrading.item.toAccount'),
            tooltipStartBtn: Resources.getResource('panel.CopyTrading.RowControl.ButtonStart.Tooltip'),
            tooltipStopBtn: Resources.getResource('panel.CopyTrading.RowControl.ButtonStop.Tooltip'),
            tooltipStartDisabled: Resources.getResource('panel.CopyTrading.RowControl.ButtonStart.Disabled.Tooltip')
        });
    }
    dispose() {
        Account.TradeStatusChanged.UnSubscribe(this.refreshPanelData, this);
        Account.FollowerIDsChanged.UnSubscribe(this.refreshPanelData, this);
        DataCache.OnAddAccount.UnSubscribe(this.refreshPanelData, this);
        DataCache.OnAccountFollowerResponse.UnSubscribe(this.onAccountFollowerResponse, this);
        Connection.unsubscribeOnConnectStateChange(this.connectionStateChange);
        super.dispose();
    }
    /**
     * Handles the Start/Stop button click event for a specific account row.
     * Sends a DISABLE_FOLLOWERS request for selected items for Stop,
     * and a FOLLOW request with all selectedFollowerIDs for Start.
     *
     * @param {number} rowIndex - The zero-based index of the account row.
     * @returns {void}
     */
    onStartOrStopButtonClick(rowIndex) {
        return __awaiter(this, void 0, void 0, function* () {
            const confirmed = !this.isUseConfirmation() || (yield this.showMessageBoxAsync(rowIndex));
            if (confirmed) {
                this.sendAccountFollowerRequest(rowIndex);
            }
            return confirmed;
        });
    }
    sendAccountFollowerRequest(rowIndex) {
        const masterAccount = this.get(`accounts[${rowIndex}]`);
        const masterFollowerIDs = new Set(masterAccount.FollowerIDs);
        const isStarted = masterFollowerIDs.size > 0;
        const selectedFollowerIDs = new Set(this.get(`selectedItems[${rowIndex}]`).map((account) => account.ID));
        const followerIDsArray = Array.from(selectedFollowerIDs);
        const filteredFollowerIDs = isStarted
            ? followerIDsArray
            : followerIDsArray.filter((id) => !masterFollowerIDs.has(id));
        if (filteredFollowerIDs.length > 0) {
            const requestArgs = {
                MasterId: masterAccount.ID,
                FollowerIDs: filteredFollowerIDs,
                Status: isStarted ? FollowerRequestStatus.DISABLE_FOLLOWERS : FollowerRequestStatus.FOLLOW
            };
            this.sendRequestWithErrorHandler(requestArgs, rowIndex, isStarted);
        }
    }
    onAccountFollowerResponse(msg) {
        var _a, _b, _c;
        if (!this.rendered) {
            return;
        }
        if (msg.IsReject) {
            const masterAccount = DataCache.GetAccountById(msg.MasterId.toString());
            const accounts = this.get('accounts');
            const rowIndex = accounts.indexOf(masterAccount);
            const isStarted = this.get(`isStarted[${rowIndex}]`);
            const rejectAccountName = (_c = (_b = DataCache.GetAccountById((_a = msg.RejectAccountID) === null || _a === void 0 ? void 0 : _a.toString())) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : '';
            this.showErrorMessageBox(!isStarted, msg.Status, rejectAccountName);
        }
    }
    sendRequestWithErrorHandler(requestArgs, rowIndex, isStarted) {
        void DataCache.SendAccountFollowerRequest(requestArgs);
    }
    showErrorMessageBox(isForStart, status, accountName) {
        const headerText = Resources.getResource('screen.error.title');
        const bodyLocalKey = (() => {
            switch (status) {
                case FollowerRequestStatus.ALREADY_FOLLOWING_ANOTHER_MASTER:
                    return 'panel.CopyTrading.Error.Reject.AlreadyFollowingMaster';
                case FollowerRequestStatus.MASTER_ACCOUNT_CANNOT_BE_FOLLOWER:
                    return 'panel.CopyTrading.Error.Reject.MasterCannotFollow';
                default:
                    return isForStart
                        ? 'panel.CopyTrading.Error.Start.Message'
                        : 'panel.CopyTrading.Error.Stop.Message';
            }
        })();
        TerceraMessageBox.Show(headerText, Resources.getResource(bodyLocalKey, [accountName]), MessageBoxType.Error, null, null, false, true);
    }
    isUseConfirmation() {
        return BaseSettingsUtils.useConfirmation(ConfirmationTypesEnum.CopyTradingStartStopFollow);
    }
    showMessageBoxAsync(rowIndex) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve) => {
                const isStopRequest = this.get('isStarted')[rowIndex];
                const headerKey = isStopRequest
                    ? 'panel.CopyTrading.Confirmation.Stop.Caption'
                    : 'panel.CopyTrading.Confirmation.Start.Caption';
                const bodyKey = isStopRequest
                    ? 'panel.CopyTrading.Confirmation.Stop.Message'
                    : 'panel.CopyTrading.Confirmation.Start.Message';
                TerceraMessageBox.Show(Resources.getResource(headerKey), Resources.getResource(bodyKey), MessageBoxType.Question, (showNext) => {
                    BaseSettingsUtils.updateConfirmation(ConfirmationTypesEnum.CopyTradingStartStopFollow, showNext);
                    resolve(true);
                }, (showNext) => {
                    BaseSettingsUtils.updateConfirmation(ConfirmationTypesEnum.CopyTradingStartStopFollow, showNext);
                    resolve(false);
                }, this.isUseConfirmation());
            });
        });
    }
    refreshPanelData() {
        const accounts = DataCache.getSortedAccountsArray();
        const ownedAccounts = Object.values(DataCache.getOwnedAccounts());
        const maxAccountTypeWidth = Math.max(...accounts.map((account) => getAccountModeWidth(account)), 0);
        void this.set({
            accounts,
            accountTypeWidth: maxAccountTypeWidth,
            selectedItems: accounts.map((account) => account.FollowerIDs.map((id) => DataCache.getOwnedAccounts()[id]).filter(Boolean)),
            isStarted: accounts.map((account) => account.FollowerIDs.length > 0),
            excludedAccounts: accounts.map((account) => [
                account,
                ...ownedAccounts.filter((ownedAcc) => !accounts.includes(ownedAcc))
            ])
        });
    }
    getBtnTooltip(index) {
        const { isStarted, selectedItems, tooltipStopBtn, tooltipStartBtn, tooltipStartDisabled } = this.get();
        return isStarted[index]
            ? tooltipStopBtn
            : selectedItems[index].length > 0
                ? tooltipStartBtn
                : tooltipStartDisabled;
    }
    getAccountModeText(account) {
        return getAccountModeText(account);
    }
    getIsEmptyType(account) {
        return account.PreferredInstrumentType === InstrumentTypes.NONE;
    }
    getAccountName(account) {
        return account.toString(true);
    }
}
ApplicationPanel.extendWith(CopyTradingPanel, {
    data: () => ({
        accounts: null, // Account[]
        selectedItems: null, // Account[][]
        isStarted: null, // - boolean[]: for each from accounts
        fromAccountText: '',
        toAccountText: '',
        btnStartText: '',
        btnStopText: '',
        tooltipStartBtn: '',
        tooltipStopBtn: '',
        resizable: false,
        dockablePanel: false,
        attached: false,
        showHeader: true,
        showFooter: false,
        style_addition_header: 'js-CopyTrading-Header',
        addtype: true,
        accountTypeWidth: 32
    }),
    partials: {
        bodyPartial: CopyTradingPanelTemplate
    }
});
